import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  EmployeeDetails,
  EmployeeWageDetails,
} from "../../interfaces/app_types";
import { useState } from "react";
import { salaryCardValidationSchema } from "../../utils/validations/validations";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { employeeTermination, updateSalaryDetails } from "../../api/salary";
import SalaryMonthCardModal from "./SalaryMonthCardModal";
import SalaryDisplay from "./SalaryDisplay";
import FormField from "../common/FormField";
import "./salary-review.css";
const SalaryCard = ({
  emp_id,
  emp_name,
  rate,
  last_increment,
  loan,
  travel,
  extra,
  loan_cut,
  sundays,
  working_hrs,
  present,
  eleven_hrs,
  one_punch,
  add_hours,
  add_cash,
  emp_notes,
}: EmployeeDetails) => {
  const queryClient = useQueryClient();
  const [disabledField, setDisabledField] = useState(true);
  const [showAttendance, setShowAttendance] = useState(false);

  const resolver = yupResolver(salaryCardValidationSchema);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = useForm<EmployeeWageDetails>({
    resolver,
    defaultValues: {
      loan: loan || 0,
      rate: rate || 0,
      last_increment: last_increment || "",
      travel: travel || 0,
      extra: extra || 0,
      loan_cut: loan_cut || 0,
      add_hours: add_hours || 0,
      add_cash: add_cash || 0,
      emp_notes: emp_notes || "",
    },
  });
  const salaryValues = watch();

  const onSubmit = (data, event) => {
    event?.preventDefault();
    if (!disabledField && !updateEmpMutation.isLoading) {
      updateEmpMutation.mutate(data);
    }
  };

  const updateEmpMutation = useMutation({
    mutationFn: async (data: EmployeeWageDetails) => {
      return updateSalaryDetails(emp_id, data);
    },
    onSuccess(_response, data) {
      reset({
        ...data,
        last_increment: getValues("last_increment"),
      });
      setDisabledField(true);
      queryClient.invalidateQueries(["salary"]);
    },
  });

  const terminationMutation = useMutation({
    mutationFn: async (empId: number) => {
      return employeeTermination(empId);
    },
    onSuccess() {
      queryClient.resetQueries({ queryKey: ["salary"] });
    },
  });

  return (
    <div
      className={`salary-card${showAttendance ? " salary-card--review" : ""}`}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="f-between p-2 salary-review-header">
          <div className="heading-2">{emp_name}</div>
          <div className="d-flex">
            <button
              type="button"
              className="styled-btn--dark me-2"
              onClick={() =>
                confirm("Are you Sure?")
                  ? terminationMutation.mutate(emp_id)
                  : ""
              }
            >
              Terminate?
            </button>
            <button
              type="button"
              className="styled-btn--std me-2"
              aria-expanded={showAttendance}
              aria-controls={`salary-attendance-${emp_id}`}
              onClick={() => {
                setShowAttendance(!showAttendance);
                if (!showAttendance) {
                  setDisabledField(false);
                  updateEmpMutation.reset();
                }
              }}
            >
              {showAttendance ? "Hide Month Card" : "Month Card"}
            </button>

            {disabledField ? (
              <button
                className="styled-btn--std"
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  setDisabledField(false);
                  updateEmpMutation.reset();
                }}
              >
                Edit
              </button>
            ) : (
              <button
                className="styled-btn--std"
                type="submit"
                disabled={updateEmpMutation.isLoading}
              >
                {updateEmpMutation.isLoading ? "Saving..." : "Save Salary"}
              </button>
            )}
          </div>
        </div>
        <div className={showAttendance ? "salary-review-layout" : ""}>
          {showAttendance && (
            <section
              className="salary-review-attendance"
              id={`salary-attendance-${emp_id}`}
              aria-label={`${emp_name} month attendance`}
            >
              <h3>Month attendance</h3>
              <p>Review attendance while updating salary and notes.</p>
              <SalaryMonthCardModal employeeId={emp_id} />
            </section>
          )}
          <section
            className={showAttendance ? "salary-review-editor" : ""}
            aria-label={`${emp_name} salary details`}
          >
            {updateEmpMutation.isError && (
              <div className="alert alert-danger" role="alert">
                Salary could not be saved. Your edits are still here; please try
                again.
              </div>
            )}
            {updateEmpMutation.isSuccess && disabledField && (
              <div className="text-success p-2" role="status">
                Salary saved.
              </div>
            )}
            <fieldset
              disabled={updateEmpMutation.isLoading}
              className="salary-review-fields"
            >
              <SalaryDisplay
                rate={Number(salaryValues.rate) || 0}
                sundays={sundays}
                travel={Number(salaryValues.travel) || 0}
                extra={Number(salaryValues.extra) || 0}
                last_increment={last_increment}
                one_punch={one_punch}
                loan_cut={Number(salaryValues.loan_cut) || 0}
                working_hrs={working_hrs}
                eleven_hrs={eleven_hrs}
                loan={loan}
                present={present}
                add_hours={Number(salaryValues.add_hours) || 0}
                add_cash={Number(salaryValues.add_cash) || 0}
                emp_notes={emp_notes}
              />
              <div className="f-wrap p-2">
                <FormField
                  title={"Rate"}
                  containerClass={showAttendance ? "d-none" : ""}
                  inputId={`salary-${emp_id}-rate`}
                  value={rate}
                  formRegister={{ ...register("rate") }}
                  disabled={disabledField}
                  fieldType="number"
                  errorMessage={errors?.rate?.message}
                />
                <FormField
                  title={"Last Increment"}
                  containerClass={showAttendance ? "d-none" : ""}
                  inputId={`salary-${emp_id}-last-increment`}
                  value={last_increment}
                  formRegister={{ ...register("last_increment") }}
                  fieldType="date"
                  disabled={disabledField}
                  errorMessage={errors?.last_increment?.message}
                />
                <FormField
                  title={"Working Hours"}
                  containerClass={showAttendance ? "d-none" : ""}
                  inputId={`salary-${emp_id}-working-hours`}
                  fieldType="display"
                  value={working_hrs}
                />
                <FormField
                  title={"Present"}
                  inputId={`salary-${emp_id}-present`}
                  fieldType="display"
                  value={present}
                />
                <FormField
                  title={"11 Hours"}
                  inputId={`salary-${emp_id}-11-hours`}
                  fieldType="display"
                  value={eleven_hrs}
                />
                <FormField
                  title={"Wrong Punch"}
                  inputId={`salary-${emp_id}-wrong-punch`}
                  fieldType="display"
                  value={one_punch}
                />
                <FormField
                  title={"Sundays"}
                  inputId={`salary-${emp_id}-sundays`}
                  fieldType="display"
                  value={sundays}
                />
                <FormField
                  title={"Extra"}
                  containerClass={showAttendance ? "d-none" : ""}
                  inputId={`salary-${emp_id}-extra`}
                  value={extra}
                  fieldType="number"
                  disabled={disabledField}
                  errorMessage={errors?.extra?.message}
                  formRegister={{ ...register("extra") }}
                />
                <FormField
                  title={"Travel"}
                  containerClass={showAttendance ? "d-none" : ""}
                  inputId={`salary-${emp_id}-travel`}
                  fieldType="number"
                  value={travel}
                  disabled={disabledField}
                  errorMessage={errors?.travel?.message}
                  formRegister={{ ...register("travel") }}
                />
                <FormField
                  title={"Add Hours"}
                  inputId={`salary-${emp_id}-add-hours`}
                  value={add_hours}
                  disabled={disabledField}
                  fieldType="number"
                  step={0.01}
                  errorMessage={errors?.add_hours?.message}
                  formRegister={{ ...register("add_hours") }}
                />
                <FormField
                  title={"Loan"}
                  containerClass={showAttendance ? "d-none" : ""}
                  inputId={`salary-${emp_id}-loan`}
                  value={loan}
                  fieldType="number"
                  disabled={disabledField}
                  errorMessage={errors?.loan?.message}
                  formRegister={{ ...register("loan") }}
                />
                <FormField
                  title={"Cut Loan"}
                  containerClass={showAttendance ? "d-none" : ""}
                  inputId={`salary-${emp_id}-cut-loan`}
                  fieldType="number"
                  value={loan_cut}
                  disabled={disabledField}
                  errorMessage={errors?.loan_cut?.message}
                  formRegister={{ ...register("loan_cut") }}
                />
                <FormField
                  title={"Add Cash"}
                  inputId={`salary-${emp_id}-add-cash`}
                  value={add_cash}
                  disabled={disabledField}
                  fieldType="number"
                  errorMessage={errors?.add_cash?.message}
                  formRegister={{ ...register("add_cash") }}
                />
                <FormField
                  fieldType="textarea"
                  title={"Notes"}
                  inputId={`salary-${emp_id}-notes`}
                  value={emp_notes}
                  labelContainerClass="col-1"
                  fullWidth={true}
                  disabled={disabledField}
                  errorMessage={errors?.emp_notes?.message}
                  formRegister={{ ...register("emp_notes") }}
                />
              </div>
              {showAttendance &&
                (errors.rate ||
                  errors.last_increment ||
                  errors.extra ||
                  errors.travel ||
                  errors.loan ||
                  errors.loan_cut) && (
                  <div className="text-danger p-2" role="alert">
                    Please hide Month Card to correct the highlighted salary
                    details before saving.
                  </div>
                )}
            </fieldset>
          </section>
        </div>
      </form>
    </div>
  );
};

export default SalaryCard;
